import { useEffect, useRef, useState } from 'react';

const problems = [
  {
    icon: 'ri-tools-line',
    title: '工具买了，效率没提升',
    desc: '团队用了 AI，但效率没有稳定提升。工具的使用停留在个人层面，无法形成团队级别的生产力提升。',
  },
  {
    icon: 'ri-code-s-slash-line',
    title: '系统想改，但改不动',
    desc: '老系统积累了大量技术债，想引入 AI 辅助开发，但代码结构混乱，AI 根本无法理解项目上下文。',
  },
  {
    icon: 'ri-flow-chart',
    title: '流程没变，工具再多也没用',
    desc: '研发流程、协作方式、交付标准都没有变化，只是在原有流程上叠加工具，效果自然有限。',
  },
];

export default function ProblemSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 md:py-32 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Left */}
          <div
            className="transition-all duration-700"
            style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateX(0)' : 'translateX(-30px)' }}
          >
            <span className="text-xs uppercase tracking-widest text-[#64748B] mb-4 block" style={{ fontFamily: 'Inter, sans-serif' }}>
              The Problem
            </span>
            <h2
              className="text-3xl md:text-5xl font-bold text-[#0F172A] leading-tight mb-8"
              style={{ fontFamily: '\'Noto Sans SC\', sans-serif', letterSpacing: '-0.02em' }}
            >
              工具已经买了，
              <br />
              为什么效果
              <br />
              <span className="text-[#64748B]">不稳定？</span>
            </h2>
            <p
              className="text-base text-[#64748B] leading-relaxed max-w-md"
              style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
            >
              很多企业已经开始使用 AI 工具，但效果参差不齐。问题不在工具本身，而在于没有完成底层的能力迁移。
            </p>

            <div className="mt-10 p-6 rounded-2xl bg-[#0F172A] text-white">
              <p className="text-sm text-white/60 mb-3" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>核心洞察</p>
              <p className="text-base font-medium leading-relaxed" style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}>
                工具是表层，能力是根本。<br />
                没有完成能力迁移，工具只是增加了复杂度。
              </p>
            </div>
          </div>

          {/* Right */}
          <div className="flex flex-col gap-5">
            {problems.map((p, idx) => (
              <div
                key={idx}
                className="bg-white rounded-2xl p-8 border border-slate-100 flex gap-5 transition-all duration-700 hover:border-[#2563EB]/30 hover:shadow-sm"
                style={{
                  opacity: visible ? 1 : 0,
                  transform: visible ? 'translateX(0)' : 'translateX(30px)',
                  transitionDelay: `${idx * 150 + 200}ms`,
                }}
              >
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#F97316]/10">
                    <i className={`${p.icon} text-[#F97316] text-lg`} />
                  </div>
                </div>
                <div>
                  <h3
                    className="text-base font-bold text-[#0F172A] mb-2"
                    style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                  >
                    {p.title}
                  </h3>
                  <p
                    className="text-sm text-[#64748B] leading-relaxed"
                    style={{ fontFamily: '\'Noto Sans SC\', sans-serif' }}
                  >
                    {p.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
